import { css } from '@emotion/react';
import { faTrash } from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, useMemo, useState,
} from 'react';
import { RoundButtonStyle } from '~/_shared/baseComponents/buttons/roundButton/roundButton.styles';
import { ImageGalleryComponent } from '~/_shared/components/imageGallery/imageGallery.component';
import { isSafari } from '~/_shared/utils/deviceDetect/deviceDetect.helpers';
import { useTranslation } from '~/_shared/utils/hooks';
import { notNullsy } from '~/_shared/utils/typeGuards';
import type {
  GalleryItem, useLocationImageGallery,
} from '~/customizeLocationPanel/useLocationImageGallery';
import type { Place } from '~/places/places.types';
import type { PlacesSearch } from '~/store/mapSettings/places/mapSettingsPlaces.state';
import { getPlacesToolCircleColors } from '../../mapTools/placesTool/placesTool.helpers';
import { placeCircleStyle } from '../../mapTools/placesTool/placesTool.styles';
import { type SidebarRoundMenuButtonProps } from '../roundButtonMenu/sidebarRoundButtonMenu.types';
import { SidebarContentContainer } from '../sidebarContent/sidebarContent.container';
import { SidebarTitleComponent } from '../sidebarTitle/sidebarTitle.component';
import { PlaceDetailItemComponent } from './placeDetailItem.component';
import type { PlaceDetailRow } from './placeDetails.helpers';

const descriptionWrapperStyle = css({
  padding: '22px 0',
});

const contentStyle = css({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  height: '100%',
});

const IMAGE_GALLERY_HEIGHT = 225;
const imageGalleryStyle = css({
  height: IMAGE_GALLERY_HEIGHT,
});

// the list takes the leftover space and scrolls, so its padding never pushes the gallery down
const detailListStyle = css({
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  gap: 12,
  minHeight: 0,
  overflowY: 'auto',
  padding: 16,
});

const galleryWrapperStyle = css({
  flexShrink: 0,
});

type PlaceDetailsComponentProps = Readonly<{
  placeSearch: PlacesSearch;
  placeIndex: number;
  place: Place;
  detailRows: ReadonlyArray<PlaceDetailRow>;
  areDetailsLoading: boolean;
  gallery?: ReturnType<typeof useLocationImageGallery>;
  onClose: () => void;
  onRemoveFromSearchResults?: () => void;
}>;

export const PlaceDetailsComponent: FC<PlaceDetailsComponentProps> = ({
  placeSearch,
  placeIndex,
  place,
  detailRows,
  areDetailsLoading,
  gallery,
  onClose,
  onRemoveFromSearchResults,
}) => {
  const [t] = useTranslation();
  const [selectedGalleryItem, setSelectedGalleryItem] = useState<GalleryItem | null>(null);

  const rightMenuButtons = useMemo(() => {
    const buttons: SidebarRoundMenuButtonProps<string>[] = [];
    if (notNullsy(onRemoveFromSearchResults)) {
      buttons.push({
        id: 'removePlaceFromSearchResultsButton',
        buttonName: 'remove-from-search-results',
        buttonStyle: RoundButtonStyle.Danger,
        icon: faTrash,
        onClick: onRemoveFromSearchResults,
        tooltipLabel: t('placesTool.place.removeFromResults'),
      });
    }
    return buttons;
  }, [onRemoveFromSearchResults, t]);

  return (
    <SidebarContentContainer
      description={(
        <SidebarTitleComponent
          onXClick={onClose}
          sidebarTitle={{
            header: place.name,
            description: [place.address],
          }}
          containerStyle={descriptionWrapperStyle}
          indicator={(
            <div
              css={placeCircleStyle(getPlacesToolCircleColors(placeSearch.color))}
            >
              {placeIndex + 1}
            </div>
          )}
        />
      )}
      menuProps={rightMenuButtons.length > 0 ? {
        selectedButton: null,
        roundButtonRightDetails: rightMenuButtons,
      } : undefined}
      isLoading={areDetailsLoading}
      body={(
        <div css={contentStyle}>
          <div css={detailListStyle}>
            {detailRows.map(row => (
              <PlaceDetailItemComponent
                key={row.id}
                label={row.label}
                value={row.value}
                href={row.href}
              />
            ))}
          </div>
          <div css={galleryWrapperStyle}>
            {gallery && (
              <ImageGalleryComponent
                items={gallery.galleryItems}
                renderItem={gallery.renderGalleryItem}
                css={imageGalleryStyle}
                onImageSelect={setSelectedGalleryItem}
                onExpandClick={selectedGalleryItem?.onExpandClick}
                triggerTooltip={selectedGalleryItem?.type === 'streetview' ? t('Launch Street View') : t('Expand Image')}
                allowOverflow={isSafari()}
              />
            )}
          </div>
        </div>
      )}
    />
  );
};
